import { TelegramAdapter } from './adapters/telegram.adapter';
import { VKAdapter } from './adapters/vk.adapter';
import { WebAdapter } from './adapters/web.adapter';
import type { DetectResult, Platform } from './platform.types';

function devOverride(): Platform | null {
  if (typeof window === 'undefined' || !window.location) return null;
  const override = new URLSearchParams(window.location.search || '').get('platform');
  if (!override) return null;
  const allowed: Platform[] = ['web', 'vk', 'telegram', 'ok', 'max'];
  if (allowed.includes(override as Platform) && import.meta.env.NODE_ENV !== 'production') {
    return override as Platform;
  }
  return null;
}

function hasTelegramLaunchSignals(): boolean {
  if (typeof window === 'undefined') return false;
  const search = window.location.search || '';
  const hash = window.location.hash || '';
  if (/tgWebApp(Data|Version|Platform|StartParam|ThemeParams)/i.test(`${search}${hash}`)) {
    return true;
  }
  const inFrame = window.self !== window.top;
  const referrer = document.referrer || '';
  return inFrame && /(^|\.)telegram\.org/i.test(referrer);
}

/** VK мини-апп: глобал контейнера или iframe с vk.com — даже без vk_* в URL (иначе уедем в web + permissive). */
function hasVkMiniAppEmbedSignals(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as Window & { VKWebApp?: unknown };
  if (w.VKWebApp != null) return true;
  const inFrame = window.self !== window.top;
  const ref = typeof document !== 'undefined' ? document.referrer || '' : '';
  return inFrame && /(^|\.)vk\.com/i.test(ref);
}

/**
 * Определяет платформу запуска. Порядок проверок гарантирует изоляцию:
 * 1. devOverride (?platform=) — только в DEV
 * 2. Telegram — window.Telegram?.WebApp или tgWebApp* в URL/referrer (приоритет над VK при коллизии)
 * 3. VK — bridge + vk_* в URL (в web-билде vk-bridge стаб, но hasVkParams всё равно нужен)
 * 4. Web — fallback
 */
export function detectPlatform(): DetectResult {
  const override = devOverride();
  if (override) {
    return { platform: override, reason: 'dev_override', confidence: 'high' };
  }

  const tgAdapter = new TelegramAdapter();
  if (tgAdapter.canDetect()) {
    const initData = typeof window !== 'undefined' ? window.Telegram?.WebApp?.initData : '';
    if (initData && typeof initData === 'string' && initData.length > 0) {
      return { platform: 'telegram', reason: 'telegram_webapp_with_initdata', confidence: 'high' };
    }
    return { platform: 'telegram', reason: 'telegram_webapp_no_initdata', confidence: 'medium' };
  }
  if (hasTelegramLaunchSignals()) {
    return { platform: 'telegram', reason: 'telegram_launch_signals', confidence: 'medium' };
  }

  // VK: vk_* в URL или явные сигналы мини-аппа (VKWebApp / iframe vk.com)
  const vkAdapter = new VKAdapter();
  if (vkAdapter.canDetect()) {
    const hasVkParams =
      typeof window !== 'undefined' &&
      /vk_(app_id|platform|ref|user_id)/i.test(window.location.search || window.location.hash || '');
    if (hasVkParams) {
      return { platform: 'vk', reason: 'vk_params_in_url', confidence: 'medium' };
    }
    if (hasVkMiniAppEmbedSignals()) {
      return { platform: 'vk', reason: 'vk_embed_signals', confidence: 'high' };
    }
  }

  // Fallback web
  const webAdapter = new WebAdapter();
  if (webAdapter.canDetect()) {
    return { platform: 'web', reason: 'fallback_web', confidence: 'high' };
  }

  return { platform: 'web', reason: 'unknown', confidence: 'low' };
}
